import Vue from 'vue';
import axios from 'axios';
import { set } from 'vue-gtag';
import { get, post, patch } from '../../request';

function getCache(key) {
  return JSON.parse(
    localStorage.getItem(`wowzi:${key}`),
  );
}

function setCache(key, val) {
  if (!val) return localStorage.removeItem(`wowzi:${key}`);
  return localStorage.setItem(
    `wowzi:${key}`,
    JSON.stringify(val),
  );
}

const createState = () => ({
  error: null,
  token: getCache('token'),
  isAdmin: getCache('isAdmin') || false,
  isLoggedIn: getCache('isLoggedIn') || false,
  profile: getCache('profile') || null,
});

const getters = {
  isAuthenticated: state => state.isLoggedIn,
  isAnonymous: state => state.profile === null,
  isDisabled: state => state.profile && state.profile.account_disabled,
};

const mutations = {
  setToken(s, token) {
    Vue.set(s, 'token', token);
    setCache('token', token);
    Vue.set(s, 'isLoggedIn', true);
    setCache('isLoggedIn', true);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  },

  setProfile(s, profile) {
    const isAdmin = profile.role === 'manager';
    Vue.set(s, 'profile', profile);
    Vue.set(s, 'isAdmin', isAdmin);
    setCache('profile', profile);
    setCache('isAdmin', isAdmin);
  },

  clearToken(s) {
    Vue.set(s, 'profile', null);
    Vue.set(s, 'token', null);
    Vue.set(s, 'isAdmin', false);
    Vue.set(s, 'isLoggedIn', false);
    setCache('profile');
    setCache('token');
    setCache('isAdmin');
    setCache('isLoggedIn');
    delete axios.defaults.headers.common.Authorization;
  },

  error(s, e) {
    Vue.set(s, 'error', e);
  },
};

const actions = {
  async doSendVerificationEmail({ commit }, form) {
    commit('clearToken');
    commit('error', null);

    try {
      await post('/hack/send_verification_email', form, { commit });
    } catch (e) {
      commit('error', e);
    }
  },

  async doLogin({ commit }, { email, password }) {
    commit('clearToken');
    commit('error', null);

    try {
      // eslint-disable-next-line camelcase
      const { access_token } = await post('/auth/login/merchant', {
        username: email,
        password,
      }, { commit });

      commit('setToken', access_token);
      commit('message', 'Welcome to Wowzi :)', { root: true });
    } catch (e) {
      commit('error', e);
    }
  },

  async doGetProfile({ commit }) {
    try {
      const profile = await get('/user/merchant', { commit });
      const { id } = profile;
      commit('setProfile', profile);
      set('userId', id);
      set('user_id', id);
    } catch (e) {
      commit('error', e);
    }
  },

  async doRequestPasswordReset({ commit }, { email }) {
    commit('clearToken');
    commit('error', null);

    try {
      await post('/auth/reset_password/merchant', { email }, { commit });
    } catch (e) {
      commit('error', e);
    }
  },

  async doResetPassword({ commit }, { email, password, token }) {
    commit('clearToken');
    commit('error', null);

    try {
      await post('/auth/save_password/merchant', { email, password, token }, { commit });
    } catch (e) {
      commit('error', e, { root: true });
    }
  },

  async doCreateAccount({ commit }, data) {
    commit('error', null);

    try {
      await post('/hack/register', data, { commit });
      const phoneData = await patch('/user/merchant/phone_number', {
        phone_number: data.phone,
      }, { commit });
      commit('setPhoneNumber', phoneData);
    } catch (e) {
      commit('error', e);
    }
  },
  async doCreateAccountFromInvite({ commit }, data) {
    commit('error', null);

    try {
      await post('/auth/create_account_from_invite/merchant', data, { commit });

      const phoneData = await patch('/user/merchant/phone_number', {
        phone_number: data.phone,
      }, { commit });
      commit('setPhoneNumber', phoneData);
    } catch (e) {
      commit('error', e);
    }
  },

  async doLogout({ commit }) {
    commit('clearToken');
  },
};

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state: createState,
};
